import { FC, MouseEventHandler, useCallback, useMemo } from 'react';

import {
  ChassisDamageTypes,
  Damage,
  EquipmentType,
  chassisDamageHasLocation,
  chassisDamageLocationOptions,
  chassisDamageWithLocationHasPart,
  containerDamageHasLocation,
  containerDamageWithLocationHasPart,
  containerSystems,
  isChassisDamage,
  isContainerDamage,
  isGensetDamage
} from 'types/damage';

import { Loader } from 'components';
import { useDamageOptions } from 'contexts/damage-options';
import { ActiveUnit } from 'types';
import { DamageType } from 'types/damage';
import { useEditTransaction } from './edit-transaction.context';

const DamageRow: FC<{ damage: Damage | null; index: number }> = ({ damage, index }) => {
  const { setTransaction } = useEditTransaction();
  const { chassisDamageMap } = useDamageOptions();

  const handleChange = useCallback(
    <T extends Damage>(p?: Partial<T>) => {
      setTransaction(transaction => {
        if (transaction && index in transaction.damages) {
          const damages = [...transaction.damages];
          const [current] = damages.splice(index, 1);
          if (p) damages.splice(index, 0, { ...current, ...p } as T);
          return { ...transaction, damages };
        } else return transaction;
      });
    },
    [index, setTransaction]
  );

  if (!damage) return null;

  return (
    <div className="damage-grid p-2">
      <div>
        <h6 className="text-info-dark">EQUIPMENT</h6>
        <input className="mt-2 w-100" type="text" defaultValue={damage.equipmentType} disabled />
      </div>
      {isContainerDamage(damage) ? (
        <>
          <div>
            <h6 className="text-info-dark">SIDE</h6>
            <select
              className="mt-2 w-100"
              value={damage.location ?? ''}
              onChange={e => handleChange({ location: e.target.value || undefined, part: undefined })}
            >
              <option value="" />
              {Object.keys(containerSystems).map(side => (
                <option value={side}>{side}</option>
              ))}
            </select>
          </div>
          <div>
            <h6 className="text-info-dark">LOCATION</h6>
            <select
              className="mt-2 w-100"
              disabled={!containerDamageHasLocation(damage)}
              value={damage.part ?? ''}
              onChange={e => handleChange({ part: e.target.value || undefined, damageType: undefined })}
            >
              <option value="" />
              {containerDamageHasLocation(damage) &&
                Object.keys(containerSystems[damage.location]).map(part => <option value={part}>{part}</option>)}
            </select>
          </div>
          <div>
            <h6 className="text-info-dark">DAMAGE</h6>
            <select
              className="mt-2 w-100"
              disabled={!containerDamageHasLocation(damage) || !containerDamageWithLocationHasPart(damage)}
              value={damage.damageType ?? ''}
              onChange={e => handleChange({ damageType: (e.target.value as DamageType) || undefined })}
            >
              <option value="" />
              {containerDamageHasLocation(damage) &&
                containerDamageWithLocationHasPart(damage) &&
                Object.assign<DamageType[], DamageType[] | undefined>(
                  [],
                  containerSystems[damage.location][damage.part]
                ).map(type => <option value={type}>{type}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <h6 className="text-info-dark">NOTES</h6>
            <input
              className="mt-2 w-100"
              type="text"
              value={damage.notes ?? ''}
              onChange={e => handleChange({ notes: e.target.value ?? undefined })}
            />
          </div>
        </>
      ) : isChassisDamage(damage) ? (
        <>
          <div>
            <h6 className="text-info-dark">SYSTEM</h6>
            <select
              className="mt-2 w-100"
              value={damage.location ?? ''}
              onChange={e =>
                handleChange({ location: e.target.value || undefined, part: undefined, subLocation: undefined })
              }
            >
              <option value="" />
              {Object.keys(chassisDamageMap).map(system => (
                <option value={system}>{system}</option>
              ))}
            </select>
          </div>
          <div>
            <h6 className="text-info-dark">COMPONENT</h6>
            <select
              className="mt-2 w-100"
              disabled={!chassisDamageHasLocation(damage)}
              value={damage.part ?? ''}
              onChange={e =>
                handleChange({
                  part: (e.target.value as keyof ChassisDamageTypes[keyof ChassisDamageTypes]) || undefined,
                  subLocation: undefined
                })
              }
            >
              <option value="" />
              {chassisDamageHasLocation(damage) &&
                Object.keys(chassisDamageMap[damage.location]).map(component => (
                  <option value={component}>{component}</option>
                ))}
            </select>
          </div>
          <div>
            <h6 className="text-info-dark">LOCATION</h6>
            <select
              className="mt-2 w-100"
              disabled={!chassisDamageHasLocation(damage) || !chassisDamageWithLocationHasPart(damage)}
              value={damage.subLocation ?? ''}
              onChange={e => handleChange({ subLocation: e.target.value || undefined })}
            >
              <option value="" />
              {chassisDamageHasLocation(damage) &&
                chassisDamageWithLocationHasPart(damage) &&
                chassisDamageLocationOptions[damage.location][damage.part]?.locations.map((subLocation: string) => (
                  <option value={subLocation}>{subLocation}</option>
                ))}
            </select>
          </div>
          <div>
            <h6 className="text-info-dark">DAMAGE</h6>
            <select
              className="mt-2 w-100"
              disabled={!chassisDamageHasLocation(damage) || !chassisDamageWithLocationHasPart(damage)}
              value={damage.damageType ?? ''}
              onChange={e => handleChange({ damageType: (e.target.value as DamageType) || undefined })}
            >
              <option value="" />
              {chassisDamageHasLocation(damage) &&
                chassisDamageWithLocationHasPart(damage) &&
                chassisDamageMap[damage.location][damage.part]?.map(part => <option value={part}>{part}</option>)}
            </select>
          </div>
          <div>
            <h6 className="text-info-dark">NOTES</h6>
            <input
              className="mt-2 w-100"
              type="text"
              value={damage.notes ?? ''}
              onChange={e => handleChange({ notes: e.target.value ?? undefined })}
            />
          </div>
        </>
      ) : isGensetDamage(damage) ? (
        <>
          <div>
            <h6 className="text-info-dark">LOCATION</h6>
            <input
              type="text"
              className="mt-2 w-100"
              value={damage.location ?? ''}
              onChange={e => handleChange({ location: e.target.value || undefined })}
            />
          </div>
          <div>
            <h6 className="text-info-dark">DAMAGE</h6>
            <select
              className="mt-2 w-100"
              value={damage.damageType ?? ''}
              onChange={e => handleChange({ damageType: (e.target.value as DamageType) || undefined })}
            >
              <option value="" />
              {Object.values(DamageType).map(type => (
                <option value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="col-span-3">
            <h6 className="text-info-dark">NOTES</h6>
            <input
              className="mt-2 w-100"
              type="text"
              value={damage.notes ?? ''}
              onChange={e => handleChange({ notes: e.target.value ?? undefined })}
            />
          </div>
        </>
      ) : null}
      <div className="mt-5">
        <button className="btn-back p-2" type="button" onClick={() => handleChange()}>
          <span className="material-icons">delete</span>
        </button>
      </div>
    </div>
  );
};

const EditDamages: FC = () => {
  const { transaction, setTransaction } = useEditTransaction();

  const damages: Damage[] = useMemo(() => Object.assign([], transaction?.damages), [transaction?.damages]);

  const units: ActiveUnit[] = useMemo(() => {
    return Object.assign([], transaction?.inbound ? transaction.gateInUnits : transaction?.gateOutUnits);
  }, [transaction?.inbound, transaction?.gateInUnits, transaction?.gateOutUnits]);

  const equipmentTypes: Set<EquipmentType> = useMemo(() => {
    const set = units
      .map(({ equipmentType }: ActiveUnit) => equipmentType)
      .reduce((set, equipmentType) => (!equipmentType ? set : set.add(equipmentType)), new Set<EquipmentType>());
    return units.some(unit => unit.sizeType?.endsWith('TR')) ? set.add(EquipmentType.CHASSIS) : set;
  }, [units]);

  const addDamage: MouseEventHandler<HTMLButtonElement> = useCallback(
    e => {
      const equipmentType = e.currentTarget.value as EquipmentType;
      setTransaction(t => t && { ...t, damages: [...damages, { equipmentType }] });
    },
    [damages, setTransaction]
  );

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>INSPECTION SUMMARY</h4>
      </div>
      <div className="card-body grid-1">
        <Loader loading={transaction === undefined}>
          {damages.map((d, i) => (
            <DamageRow key={d.id ?? i} index={i} damage={d} />
          ))}
        </Loader>
        <div className="d-flex g-3 p-2">
          <button
            className="btn-outline mt-2"
            type="button"
            value={EquipmentType.CONTAINER}
            onClick={addDamage}
            disabled={!transaction || !equipmentTypes.has(EquipmentType.CONTAINER)}
          >
            ADD CONTAINER DAMAGE
          </button>
          <button
            className="btn-outline mt-2"
            type="button"
            value={EquipmentType.CHASSIS}
            onClick={addDamage}
            disabled={!transaction || !equipmentTypes.has(EquipmentType.CHASSIS)}
          >
            ADD CHASSIS DAMAGE
          </button>
          <button
            className="btn-outline mt-2"
            type="button"
            value={EquipmentType.GENSET}
            onClick={addDamage}
            disabled={!transaction || !equipmentTypes.has(EquipmentType.GENSET)}
          >
            ADD GENSET DAMAGE
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDamages;
